import { useCallback, useEffect, useRef, useState } from "react";
import { shouldPublishFix, type LatLng } from "@/lib/geo";

export type GeoStatus = "idle" | "prompting" | "granted" | "denied" | "unavailable";

/**
 * Why the last request failed — set only when `status` is "denied" or
 * "unavailable", null otherwise. Lets the UI say the actual reason (and show a
 * retry only where retrying can help) instead of one generic "unavailable".
 */
export type GeoFailReason =
  | "denied"
  | "timeout"
  | "position_unavailable"
  | "unsupported"
  | "insecure_context";

export interface GeolocationState {
  status: GeoStatus;
  coords: LatLng | null;
  /** Reported accuracy of the last published fix, in metres (null until a fix). */
  accuracy: number | null;
  /** Failure detail — non-null only when status is "denied" or "unavailable". */
  reason: GeoFailReason | null;
  /** Ask the browser for the user's location (the tap-to-enable trigger). */
  request: () => void;
}

const GEO_OPTS: PositionOptions = { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 };
// Fallback used when the high-accuracy fix times out (routine indoors and on
// desktops without GPS): coarser sources (wifi/IP) with a longer leash.
const LO_FI_OPTS: PositionOptions = { enableHighAccuracy: false, timeout: 20_000, maximumAge: 60_000 };

export interface UseGeolocationOptions {
  /**
   * "auto" (default) — request permission on mount; the browser prompt appears
   * as the page opens (the /app feed behavior).
   * "silent" — never prompt: a position is fetched only when the Permissions
   * API already reports "granted" (the Welcome city gate must not greet
   * first-time visitors with a permission dialog).
   * "off" — never touch the Geolocation API (the `/showcase-cluj` demo, which
   * serves a fixed position instead).
   */
  mode?: "auto" | "silent" | "off";
}

/**
 * Browser geolocation that asks on app launch.
 *
 * On mount we call `request()` so the browser's native location prompt appears
 * as the app opens; on grant we switch to a live `watchPosition` so the "closest
 * to you" order tracks the user as they move. A previously-granted permission
 * resumes silently (no prompt); a previously-denied one is surfaced immediately
 * without firing a doomed request. Denied / unsupported are discrete statuses so
 * the UI shows a graceful fallback (with a re-enable button) instead of an empty
 * strip. `request()` stays exported for that re-enable.
 *
 * Hardening (the "tapped enable and nothing happened" reports):
 *  - insecure contexts (http / some in-app webviews) fail silently in browsers;
 *    we detect them up front and surface `reason: "insecure_context"`.
 *  - a high-accuracy TIMEOUT gets ONE low-accuracy retry per request() before
 *    declaring unavailable (no re-prompt — the grant was already exercised).
 *  - `reason` distinguishes denied / timeout / position_unavailable /
 *    unsupported / insecure_context so the UI can give a concrete hint.
 */
export function useGeolocation({ mode = "auto" }: UseGeolocationOptions = {}): GeolocationState {
  const [status, setStatus] = useState<GeoStatus>("idle");
  const [coords, setCoords] = useState<LatLng | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [reason, setReason] = useState<GeoFailReason | null>(null);
  const watchId = useRef<number | null>(null);
  const lastCoords = useRef<LatLng | null>(null);
  const lastAccuracy = useRef<number | null>(null);
  const loFiTried = useRef(false);

  const onPos = useCallback((pos: GeolocationPosition) => {
    setStatus("granted");
    setReason(null);
    const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
    const acc = Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null;
    // Publish the first fix, a ~25 m move, or a markedly sharper fix in place
    // (shouldPublishFix), so the distance ranking doesn't thrash on every GPS
    // tick. Accuracy rides the same gate so consumers (dwell detection, the
    // bonus stamp's accuracy check) see the accuracy of the served fix.
    if (shouldPublishFix(lastCoords.current, lastAccuracy.current, next, acc)) {
      lastCoords.current = next;
      lastAccuracy.current = acc;
      setCoords(next);
      setAccuracy(acc);
    }
  }, []);

  const onErr = useCallback((err: GeolocationPositionError) => {
    console.info("[geo] error", { code: err.code, message: err.message });
    if (err.code === err.PERMISSION_DENIED) {
      // Mid-session revocation: drop the fix too, so the UI stops showing
      // distances the user just retracted.
      lastCoords.current = null;
      lastAccuracy.current = null;
      setCoords(null);
      setAccuracy(null);
      setStatus("denied");
      setReason("denied");
      return;
    }
    // Transient watch failure (GPS timeout / position unavailable — routine
    // indoors). If a fix already landed, keep serving it: a seconds-stale
    // position beats collapsing the "closest to you" UI on one flaky tick —
    // and `reason` must not flip under a live UI either.
    if (!lastCoords.current) {
      setStatus("unavailable");
      setReason(err.code === err.TIMEOUT ? "timeout" : "position_unavailable");
    }
  }, []);

  const startWatch = useCallback(
    (opts: PositionOptions = GEO_OPTS) => {
      if (watchId.current != null || !("geolocation" in navigator)) return;
      watchId.current = navigator.geolocation.watchPosition(onPos, onErr, opts);
    },
    [onPos, onErr],
  );

  const request = useCallback(() => {
    // Geolocation silently fails outside secure contexts (http, some in-app
    // webviews). Strict `=== false` so ancient browsers without the property
    // skip the check.
    if (window.isSecureContext === false) {
      console.info("[geo] insecure context — geolocation unavailable");
      setStatus("unavailable");
      setReason("insecure_context");
      return;
    }
    if (!("geolocation" in navigator)) {
      setStatus("unavailable");
      setReason("unsupported");
      return;
    }
    setReason(null);
    loFiTried.current = false; // fresh fallback budget per request attempt
    setStatus((s) => (s === "granted" ? s : "prompting"));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onPos(pos);
        startWatch();
      },
      (err) => {
        // One low-accuracy retry on a high-accuracy timeout. No re-prompt risk:
        // the permission was already granted (or the error would be DENIED)
        // inside this same user-initiated/auto flow. Status stays "prompting"
        // while the retry runs. The watch then also starts low-accuracy —
        // hardware that starved the first fix would starve the watch too.
        if (err.code === err.TIMEOUT && !loFiTried.current && !lastCoords.current) {
          loFiTried.current = true;
          console.info("[geo] high-accuracy timeout — retrying low-accuracy");
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              onPos(pos);
              startWatch(LO_FI_OPTS);
            },
            onErr,
            LO_FI_OPTS,
          );
          return;
        }
        onErr(err);
      },
      GEO_OPTS,
    );
  }, [onPos, onErr, startWatch]);

  // Ask for location on launch. If the Permissions API says we're already
  // denied, skip the doomed request and just surface the denied state; otherwise
  // (granted → silent resume, or prompt → ask now) fire the request. Browsers
  // without the Permissions API just get asked directly. In "silent" mode the
  // request only fires on a confirmed prior grant — "prompt" (and no Permissions
  // API at all) stays idle, because asking would pop the dialog.
  useEffect(() => {
    if (mode === "off") return;
    let cancelled = false;
    if (window.isSecureContext === false) {
      // Surface it immediately (both modes) — a gesture-bound request() would
      // hit the same wall, and the UI should say why instead of doing nothing.
      setStatus("unavailable");
      setReason("insecure_context");
    } else {
      const perms = navigator.permissions;
      if (perms?.query) {
        perms
          .query({ name: "geolocation" as PermissionName })
          .then((res) => {
            if (cancelled) return;
            if (res.state === "denied") {
              setStatus("denied");
              setReason("denied");
            } else if (res.state === "granted" || mode === "auto") request();
          })
          .catch(() => {
            if (!cancelled && mode === "auto") request();
          });
      } else if (mode === "auto") {
        request();
      }
    }
    return () => {
      cancelled = true;
      if (watchId.current != null) {
        navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
      }
    };
    // request/startWatch are stable; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, coords, accuracy, reason, request };
}
